import Helper from '@/utils/helper'
import LoadingGuiManager from '@/app/mixins/loading-gui-manager'
import TimerController from '@/app/timer/timer-controller'
import { VIEWS } from '@/views/names'
import { mapActions, mapState } from 'vuex'
import * as types from '@/store/types.js'
import { eventBus, eventType } from '@/app/event-bus.js'
import Cookies from 'js-cookie'
import AdTextController from '@/views/table/queue/ad-text-controller'
import PaginatorController from '@/views/table/queue/paginator-controller'
import { colorSchemes, layouts } from '@/app/const/settings'
import ClientsQueueConverter from '@/app/model/clients/converter/clients-queue-converter'

const INTERVAL_RECONNECT = 300000
const DEBOUNCE_UPDATE = 300
const GUI_UPDATE_TIMEOUT = 1800000

const AD_POLICY_DEFAULT = 'DEFAULT'
const AD_POLICY_EMPTY_CLIENTS = 'EMPTY_CLIENTS'
const AD_POLICY_ALWAYS = 'ALWAYS'
const AD_POLICY_ONLY_AD = 'ONLY_AD'

export default {
  name: VIEWS.table.queue,
  mixins: [LoadingGuiManager, TimerController, AdTextController, PaginatorController],
  data () {
    return {
      idBranch: 'default',
      trackList: [],
      trySetClient: false,
      loadingDataError: false,
      tryConnectSocket: false,
      accessDeniedInterval: null,
      accessDeniedError: false,
      lockedUpdateInterval: true,
      guiSetting: null,
      readyToUpdate: false
    }
  },
  computed: {
    ...mapState({
      tickets: state => state.board.tickets,
      logo: state => state.gui.settings.colorScheme.logo,
      isAdPolicyDefault: state => state.gui.settings.colorScheme.adPolicy === AD_POLICY_DEFAULT,
      isAdPolicyEmptyClients: state => state.gui.settings.colorScheme.adPolicy === AD_POLICY_EMPTY_CLIENTS,
      isAdPolicyAlways: state => state.gui.settings.colorScheme.adPolicy === AD_POLICY_ALWAYS,
      isAdPolicyOnlyAd: state => state.gui.settings.colorScheme.adPolicy === AD_POLICY_ONLY_AD,
      hideHeader: state => state.gui.header,
      layout: state => state.gui.settings.layout.code
    }),
    layoutCol2DiffClients () {
      return this.layout === layouts.col2DiffClients
    },
    hiddenHeader () {
      return this.hideHeader
    },
    definedSocketConnect () {
      return Helper.isDefined(this.$store.state.network.socket.connected)
    },
    socketConnect () {
      return this.$store.state.network.socket.connected
    },
    clientsEmpty () {
      if (this.layoutCol2DiffClients) {
        return !this.currentTickets.getItems().some((item) => item.isExistTickets())
      }
      return Helper.isEmpty(this.currentTickets)
    },
    hiddenLogo () {
      return (this.$store.state.gui.settings.colorScheme.code === colorSchemes.mts && this.clientsEmpty)
    },
    showAdv () {
      return (this.isAdPolicyDefault || this.isAdPolicyOnlyAd || this.isAdPolicyAlways) && !this.playListEmpty
    },
    showLogoFullScreen () {
      return (this.isAdPolicyDefault || this.isAdPolicyAlways || this.playListEmpty) && this.clientsEmpty
    }
  },
  watch: {
    tickets () {
      this.startPagePaginator()
      if (Helper.isEmpty(this.tickets)) {
        this.readyToUpdate = Helper.isDefined(this.guiSetting)
      } else if (this.readyToUpdate) {
        this.setGuiSetting()
      }
    }
  },
  methods: {
    ...mapActions({
      lastActionTimeUpdate: types.TIMER.lastActionTime.update,
      playlistItemsSet: types.PLAYLIST.items.set,
      boardTicketsSet: types.BOARD.tickets.set,
      clientQueueSet: types.BOARD.clients.queue.set,
      playListItemsSet: types.PLAYLIST.items.set
    }),
    updateClientsBySocket (model) {
      clearInterval(this.lockedUpdateInterval)
      this.lockedUpdate = true
      this.lastActionTimeUpdate()
      this.boardTicketsSet(model)
      this.updateClientQueue(model)

      this.lockedUpdateInterval = setTimeout(() => {
        this.playListItemsSet(this.tickets)
      }, DEBOUNCE_UPDATE)
    },
    updateClientsByHttp () {
      clearInterval(this.accessDeniedInterval)
      clearInterval(this.lockedUpdateInterval)
      this.lastActionTimeUpdate()
      return this.$service
        .getState()
        .then(model => {
          this.loadingDataError = false
          this.accessDeniedError = false
          this.boardTicketsSet(model)
          this.updateClientQueue(model)

          this.lockedUpdateInterval = setTimeout(() => {
            this.playListItemsSet(this.tickets)
          }, DEBOUNCE_UPDATE)
        })
        .catch(e => {
          this.loadingDataError = true
          // Если доступ запрещен пробуем еще раз
          if (e.isAccessDenied()) {
            this.accessDeniedError = true
            this.accessDeniedInterval = setInterval(() => {
              this.updateClientsByHttp()
            }, INTERVAL_RECONNECT)
          }
        })
    },
    onTimerEnded () {
      this.updateClientsByHttp()
    },
    updateGuiSetting () {
      setTimeout(() => {
        this.getGuiSetting()
        this.readyToUpdate = this.clientsEmpty
      }, GUI_UPDATE_TIMEOUT)
    },
    getGuiSetting () {
      return this.$service.getSetting()
        .then(data => {
          this.guiSetting = data
          this.$service.updateGuiSetting(data)
          this.updateGuiSetting()
        })
        .catch(() => { /* do nothing */ })
    },
    setGuiSetting () {
      this.$service.updateGuiSetting(this.guiSetting)
      this.guiSetting = null
      this.readyToUpdate = false
    },
    updateClientQueue (model) {
      if (this.layoutCol2DiffClients) {
        this.clientQueueSet(ClientsQueueConverter.convert(model))
      }
    }
  },
  beforeMount () {
    this.lastActionTimeUpdate()
    try {
      const { idBranch } = JSON.parse(Cookies.get('userData'))
      this.idBranch = idBranch
    } catch (e) {
      // do nothing
    }
  },
  mounted () {
    eventBus.subscribe(eventType.updateClients, this.updateClientsBySocket)
    this.updateClientsByHttp().finally(() => {
      this.trySetClient = true
      this.$service.subscribeUpdateState()
      this.tryConnectSocket = true
    })
    this.updateGuiSetting()
    this.startPagePaginator()
  },
  beforeDestroy () {
    this.$service.disconnect()
    clearInterval(this.accessDeniedInterval)
    eventBus.off(eventType.updateClients)
  }
}
